import React from 'react';
import { MultiValue } from 'react-select';

export enum InputsEnum {
  AUTOCOMPLETE = 'AUTOCOMPLETE',
  INPUT = 'INPUT',
  INPUT_FILE = 'INPUT_FILE',
  COUNTER = 'COUNTER',
  TEXTAREA = 'TEXTAREA',
  SELECT = 'SELECT',
  CHECK = 'CHECK',
  DATE = 'DATE',
  TAGS_INPUT = 'TAGS_INPUT',
  MASKED = 'MASKED',
  RICH_TEXT = 'RICH_TEXT',
  MULTI_SELECT = 'MULTI_SELECT',
  TIME_RANGE_PICKER = 'TIME_RANGE_PICKER',
  DATE_TIME_PICKER = 'DATE_TIME_PICKER',
  TRAINING_INPUT_FILE = 'TRAINING_INPUT_FILE',
  ASSIGN_LEADER_INPUT = 'ASSIGN_LEADER_INPUT',
  ALTERNATIVE_TEXTAREA = 'ALTERNATIVE_TEXTAREA',
  GOOGLE_MAPS_AUTOCOMPLETE = 'GOOGLE_MAPS_AUTOCOMPLETE'
}

export enum TextInputTypes {
  TEXT = 'text',
  NUMBER = 'number',
  PHONE = 'phone',
  PASSWORD = 'password',
  EMAIL = 'email'
}

export enum DateInputTypes {
  DATE = 'date',
  DATE_TIME = 'datetime-local'
}

export type CommonProps = {
  onChange: (value: any, name?: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyDown?: (e: any) => void;
  setDirtyError?: (flag: boolean) => void;
  label?: string;
  labelTooltip?: string;
  placeholder?: string;
  error?: string;
  pristine?: boolean;
  required?: boolean;
  disabled?: boolean;
  hide?: boolean;
};

export type Option = {
  label: string;
  value: string;
  errors?: string[];
};

export type TextInputProps = {
  value: string | number;
  type: TextInputTypes;
  name?: string;
  placeholder?: string;
  limit?: number;
  accept?: string;
  existingFile?: { name: string; url: string } | null;
} & CommonProps;

export type CounterInputProps = {
  value: string | number;
  name?: string;
  limit?: number;
} & CommonProps;

export type SelectProps = {
  value: any;
  options: Option[];
  style?: React.CSSProperties;
} & CommonProps;

export type TagsInputProps = {
  value: string[];
  addKeys?: number[]; // key codes to trigger add (e.g., 9=Tab, 13=Enter, 188=Comma)
  addOnBlur?: boolean; // add pending value on blur
} & CommonProps;

export type AutocompleteProps = {
  value: string;
  options?: Option[];
  endpoint: any;
  detail?: any;
  limit?: number;
  defaultLabel?: string;
  timezone?: boolean;
  fullValue?: boolean;
  isClearable?: boolean;
  avoidFirstLoad?: boolean;
  extraParams?: Record<string, any>;
} & CommonProps;

export type MultiSelectProps = {
  value: MultiValue<Option>;
  staticOptions?: Option[];
  endpoint?: any; // optional: if provided, component will fetch; otherwise uses staticOptions only
  limit?: number;
  avoidFirstLoad?: boolean;
  endpointParams?: any;
  formatOptionLabel?: (option: Option, formatLabelContext?: any) => React.ReactNode;
} & CommonProps;

export type TextareaProps = {
  value: string;
  name?: string;
  limit?: number;
  placeholder?: string;
} & CommonProps;

export type CheckProps = {
  value: boolean;
  options: Option[];
} & CommonProps;

export type DatePickerProps = {
  type: DateInputTypes;
  name?: string;
  value: Date;
  allowedDates?: Date[];
  maxDate?: Date;
} & CommonProps;

export type TimeRangePickerProps = {
  value: [string | null, string | null];
  name?: string;
} & CommonProps;

export type MaskedInputProps = {
  value: string | number | any[];
  name?: string;
  disabled?: boolean;
  placeholder?: string;
  mask: (string | RegExp)[];
  clearOnDelete?: boolean;
} & CommonProps;

export type RichTextProps = {
  value: string;
  placeholder?: string;
  limit?: number;
} & CommonProps;

export type AssignLeaderInputProps = {
  value: any[];
} & CommonProps;

export type GoogleMapsAutocompleteProps = {
  value: {
    name: string;
    formattedAddress?: string;
    address: {
      lineOne: string;
      lineTwo: string;
      city: string;
      state: string;
      postalCode: string;
    };
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  onLocationSelect?: (location: any) => void;
} & CommonProps;
